/**
 * Liste des auto-évaluations — Lot 4a + polish Lot 12.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  assessmentApi,
  campaignApi,
  ApiRequestError,
  type AssessmentSummaryDto,
  type CampaignDto,
} from "../api/api";
import { PageHeader } from "../components/ui/PageHeader";
import { StatusBadge } from "../components/ui/StatusBadge";
import { useAuth } from "../context/AuthContext";
import { naturalCompare, naturalSortByDomain } from "../utils/natural-sort";

function campaignStatusClass(status: string): string {
  if (status === "open") return "sm-badge-pill sm-badge-green";
  if (status === "closed") return "sm-badge-pill sm-badge-gray";
  if (status === "prep") return "sm-badge-pill sm-badge-yellow";
  return "sm-badge-pill sm-badge-gray";
}

export function AssessmentListPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const showNew =
    user && (user.accessProfile === "expert" || user.accessProfile === "admin");

  const [items, setItems] = useState<AssessmentSummaryDto[]>([]);
  const [campaigns, setCampaigns] = useState<CampaignDto[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await assessmentApi.list();
        if (!cancelled) setItems(res.assessments);

        const year = new Date().getFullYear();
        const domains = [...new Set((user?.domains ?? []).map((d) => d.trim()).filter(Boolean))].sort(
          naturalCompare
        );
        if (domains.length > 0) {
          const results = await Promise.all(
            domains.map((d) => campaignApi.current(year, d).catch(() => ({ campaign: null })))
          );
          if (!cancelled) {
            setCampaigns(
              naturalSortByDomain(
                results.map((r) => r.campaign).filter((c): c is CampaignDto => c != null)
              )
            );
          }
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiRequestError ? err.message : t("assessments_load_failed"));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.domains, t]);

  return (
    <div className="sm-page">
      <PageHeader
        title={t("assessments")}
        subtitle={t("assessments_sub")}
        actions={
          showNew ? (
            <Link to="/assessments/new" className="sm-btn sm-btn-primary">
              {t("new_assessment")}
            </Link>
          ) : undefined
        }
      />

      {campaigns.length > 0 ? (
        <div className="sm-card sm-card-pad" style={{ marginBottom: 12 }}>
          {campaigns.map((c) => (
            <div
              key={c.id}
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 8,
                alignItems: "center",
                margin: "6px 0",
              }}
            >
              <span className={campaignStatusClass(c.status)}>{c.status}</span>
              <strong>
                {t("campaign_banner", {
                  domain: c.domain,
                  year: c.year,
                  status: c.status,
                })}
              </strong>
              {c.submitDeadline ? (
                <span className="sm-muted">
                  — {t("campaign_deadline", { date: c.submitDeadline })}
                </span>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}

      {error ? (
        <div className="sm-alert sm-alert-error" role="alert">
          {error}
        </div>
      ) : null}

      <div className="sm-card">
        <div className="sm-table-wrap">
          <table className="sm-table">
            <thead>
              <tr>
                <th>{t("full_name")}</th>
                <th>{t("domain")}</th>
                <th>{t("erp")}</th>
                <th>{t("year")}</th>
                <th>{t("scores_progress")}</th>
                <th>{t("status")}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="sm-muted" style={{ textAlign: "center", padding: 24 }}>
                    {t("loading")}
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={7} className="sm-muted" style={{ textAlign: "center", padding: 24 }}>
                    {t("no_data")}
                  </td>
                </tr>
              ) : (
                items.map((a) => (
                  <tr key={a.id}>
                    <td>
                      {a.userName}
                      <div className="sm-muted" style={{ fontSize: 12 }}>
                        {a.userInternalId}
                      </div>
                    </td>
                    <td>{a.domain}</td>
                    <td>{a.applicationLabel}</td>
                    <td>{a.year}</td>
                    <td>
                      {a.scoredCount}/{a.lineCount}
                    </td>
                    <td>
                      <StatusBadge status={a.status} />
                    </td>
                    <td>
                      <Link to={`/assessments/${a.id}`} className="sm-link">
                        {t("open")}
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
